import { Injectable, OnDestroy, Optional, InjectionToken, Inject } from '@angular/core';
import {
  Observable,
  Subject,
  interval as observableInterval,
  of,
  EMPTY
} from 'rxjs';
import { filter, startWith, switchMap } from 'rxjs/operators';

export const LOCAL_STORAGE = new InjectionToken<Storage>('localStorage');

@Injectable()
export class LocalStorageService implements OnDestroy {
  private updateNotification: Subject<[string, string, any]>;

  constructor(@Optional() @Inject(LOCAL_STORAGE) private readonly storage?: Storage) {
    if (!storage) {
      this.storage = localStorage;
    }
  }

  set(key: string, object: any): void {
    const json = JSON.stringify(object);
    this.storage.setItem(key, json);

    if (this.updateNotification) {
      this.updateNotification.next(['set', key, object]);
    }
  }

  get<T = any>(key: string, defaultValue: T = null): T {
    try {
      const json = this.storage.getItem(key);
      if (!json) {
        return defaultValue;
      }
      return JSON.parse(json);
    } catch (e) {
      return defaultValue;
    }
  }

  remove(key: string): void {
    this.storage.removeItem(key);
    if (this.updateNotification) {
      this.updateNotification.next(['remove', key, undefined]);
    }
  }

  /**
   * Returns an observable stream that pushes every change to the specified
   * localStorage key, including the initial value.
   *
   * Usage:
   *
   * localStorageService.watch('mykey', { defaultValue: new Array<string>() }).subscribe(array => {
   *   ...
   * });
   *
   * @param key The localStorage key to watch.
   * @param options Additional options:
   *        - interval: The interval to check the localStorage key.
   *        - defaultValue: The value to use if the localStorage key is not set
   */
  watch<T = any>(key: string, options?: { interval?: number; defaultValue?: T; }): Observable<T> {
    // console.log(`[LocalStorageService] starting watch("${key}")`, new Error().stack);

    let previousJson: string; // = undefined, so !== first json read from localStorage
    const interval = (options ? options.interval : null) || 500;
    const defaultValue = (options ? options.defaultValue : null);

    if (!this.updateNotification) {
      this.updateNotification = new Subject<[string, string, any]>();
    }

    const keyUpdateObservable = this.updateNotification.pipe(
      filter(update => update[1] === key),
      startWith(['initial', key, undefined])
    );
    const intervalObservable = observableInterval(interval).pipe(startWith(0));

    return keyUpdateObservable.pipe(
      switchMap(() => intervalObservable),
      switchMap(() => {
        const newJson = this.storage.getItem(key);

        if (newJson !== previousJson) {
          previousJson = newJson;

          if (!newJson && defaultValue) {
            return of(defaultValue);
          }

          try {
            return of(JSON.parse(newJson));
          } catch (e) {
          }
        }

        return EMPTY;
      })
    );
  }

  ngOnDestroy(): void {
    if (this.updateNotification) {
      this.updateNotification.unsubscribe();
      this.updateNotification = null;
    }
  }
}
